import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, NAV_LINKS } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || open;
  const lightHeader = pathname === "/" && !solid;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${solid
        ? "bg-white/80 backdrop-blur-xl border-b border-[#e7e9ef] shadow-[0_8px_30px_rgba(29,61,143,0.06)]"
        : "bg-transparent border-b border-transparent"
        }`}
    >
      <div className="nc-container flex items-center justify-between h-[clamp(64px,7vw,88px)]">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img
            src={lightHeader ? BRAND.logoLight : BRAND.logo}
            alt="North Cadence"
            className="h-[clamp(80px,14vw,160px)] w-auto max-h-full"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-base font-medium transition-colors duration-300 ${lightHeader
                  ? isActive ? "text-white" : "text-white/75 hover:text-white"
                  : isActive ? "text-[#142A5E]" : "text-[#1B1B1B]/70 hover:text-[#142A5E]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-[2px] rounded-full bg-[#2FA86A]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-consultation"
            className={`group inline-flex items-center gap-2 rounded-full px-3 py-2 text-base font-semibold transition-colors duration-300 ${lightHeader
              ? "bg-[#2FA86A] text-white hover:bg-[#248a5a]"
              : "bg-[#142A5E] text-white hover:bg-[#0d1c40]"
              }`}
          >
            Book a Discovery Call
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#e7e9ef] bg-white text-[#142A5E]"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#e7e9ef]"
          >
            <div className="nc-container py-4 flex flex-col">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3.5 text-lg font-medium border-b border-[#f1f3f8] ${isActive ? "text-[#142A5E]" : "text-[#1B1B1B]/75"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#142A5E] px-5 py-3.5 text-base font-semibold text-white"
              >
                Book a Discovery Call <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
